/**
 * AP-MATCH-01C: Finance Invoice Review Queue
 * Route: /ap/invoice-review (internal Finance — FINANCE_APPROVER, TENANT_ADMIN)
 *
 * Lists invoices in the Finance AP workflow: SUBMITTED → VALIDATING → MATCHED | EXCEPTION → APPROVED | REJECTED
 * Actions: Trigger Matching, View Detail, Filter by status.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  FileText, ChevronRight, RefreshCw, Play,
  CheckCircle2, XCircle, Clock, AlertTriangle, Send, Archive,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  SUBMITTED:    { label: "Submitted",    bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", Icon: Send },
  VALIDATING:   { label: "Validating",   bg: "#FEF3C7", text: "#92400E", border: "#FDE68A", Icon: Clock },
  MATCHED:      { label: "Matched",      bg: "#F0FDF4", text: "#166534", border: "#BBF7D0", Icon: CheckCircle2 },
  EXCEPTION:    { label: "Exception",    bg: "#FFF7ED", text: "#9A3412", border: "#FED7AA", Icon: AlertTriangle },
  APPROVED:     { label: "Approved",     bg: "#F0FDF4", text: "#14532D", border: "#86EFAC", Icon: CheckCircle2 },
  REJECTED:     { label: "Rejected",     bg: "#FEF2F2", text: "#991B1B", border: "#FECACA", Icon: XCircle },
  VOIDED:       { label: "Voided",       bg: "#F5F5F5", text: "#9CA3AF", border: "#E5E7EB", Icon: Archive },
};

const FILTERS = [
  { label: "All",         value: "" },
  { label: "Submitted",   value: "SUBMITTED" },
  { label: "Matched",     value: "MATCHED" },
  { label: "Exception",   value: "EXCEPTION" },
  { label: "Approved",    value: "APPROVED" },
  { label: "Rejected",    value: "REJECTED" },
];

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(minor / 100);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, bg: "#F5F5F5", text: "#666", border: "#DDD", Icon: FileText };
  const Ic = cfg.Icon;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: 600,
      background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`,
    }}>
      <Ic size={11} />
      {cfg.label}
    </span>
  );
}

export default function FinanceInvoiceQueue() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [triggering, setTriggering] = useState({});
  const PAGE_SIZE = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/api/ap/match/queue`, { params, withCredentials: true });
      setInvoices(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load invoice queue");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  async function triggerMatch(invoiceId) {
    setTriggering((prev) => ({ ...prev, [invoiceId]: true }));
    try {
      const res = await axios.post(`${API}/api/ap/match/trigger/${invoiceId}`, {}, { withCredentials: true });
      toast.success(`Matching complete: ${res.data.match_status} (${res.data.passed_count} rules passed)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Matching failed");
    } finally {
      setTriggering((prev) => ({ ...prev, [invoiceId]: false }));
    }
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="finance-invoice-queue-page" style={{ minHeight: "100vh", background: "#F8F9FA", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Internal Finance Nav */}
      <nav style={{ background: "#1A1A1A", borderBottom: "1px solid rgba(201,161,95,0.2)", padding: "0 24px", height: "60px", display: "flex", alignItems: "center", gap: "16px", position: "sticky", top: 0, zIndex: 10 }}>
        <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "18px", fontWeight: 600, color: "#C9A15F" }}>
          Prashasta
        </span>
        <span style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)" }} />
        <span style={{ fontSize: "12px", color: "#8A7A6A", letterSpacing: "1px", textTransform: "uppercase" }}>Finance AP</span>
        <span style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)" }} />
        {[
          { label: "Acceptance Queue", path: "/ap/acceptance" },
          { label: "Invoice Review",   path: "/ap/invoice-review" },
        ].map(({ label, path }) => (
          <button key={path} onClick={() => navigate(path)}
            style={{ fontSize: "13px", background: "none", border: "none", cursor: "pointer", padding: "4px 8px",
              color: path === "/ap/invoice-review" ? "#F5EFE6" : "#C9A15F",
              borderBottom: path === "/ap/invoice-review" ? "2px solid #C9A15F" : "2px solid transparent",
            }}>
            {label}
          </button>
        ))}
        <div style={{ marginLeft: "auto", fontSize: "12px", color: "#6B5B4A" }}>
          {user?.email}
        </div>
      </nav>

      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px 24px" }}>
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(20px,3vw,28px)", fontWeight: 600, color: "#1A1A1A", margin: "0 0 4px" }}>
            Invoice Review Queue
          </h2>
          <p style={{ fontSize: "13px", color: "#6B5B4A", margin: 0 }}>
            {total} invoice{total !== 1 ? "s" : ""} in the AP Finance workflow — three-way matching and approval
          </p>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap", alignItems: "center" }}>
          {FILTERS.map(({ label, value }) => (
            <button key={value || "all"} data-testid={`filter-${value || "all"}`}
              onClick={() => { setStatusFilter(value); setPage(1); }}
              style={{
                padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 500,
                cursor: "pointer", border: "1px solid",
                background: statusFilter === value ? "#1A1A1A" : "transparent",
                color: statusFilter === value ? "#F5EFE6" : "#6B5B4A",
                borderColor: statusFilter === value ? "#1A1A1A" : "#DDD",
              }}>
              {label}
            </button>
          ))}
          <button onClick={load} disabled={loading} data-testid="refresh-queue-btn"
            style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "4px", padding: "6px 12px", background: "transparent", border: "1px solid #DDD", borderRadius: "20px", cursor: "pointer", fontSize: "12px", color: "#6B5B4A" }}>
            <RefreshCw size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            Refresh
          </button>
        </div>

        {/* Table */}
        <div style={{ background: "#FFF", border: "1px solid #E5E7EB", borderRadius: "12px", overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: "60px", textAlign: "center", color: "#9CA3AF", fontSize: "14px" }}>Loading…</div>
          ) : invoices.length === 0 ? (
            <div data-testid="empty-queue-state" style={{ padding: "60px", textAlign: "center" }}>
              <CheckCircle2 size={40} style={{ color: "#BBF7D0", marginBottom: "16px" }} />
              <p style={{ fontSize: "15px", color: "#6B5B4A", margin: 0 }}>
                {statusFilter ? `No invoices with status "${statusFilter}"` : "Invoice queue is clear"}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#F9FAFB" }}>
                    {["AP Reference", "Supplier Invoice#", "Submitted", "Total", "Status", "Action", ""].map((h, i) => (
                      <th key={i} style={{ padding: "12px 16px", textAlign: i === 3 ? "right" : "left", fontSize: "11px", fontWeight: 700, color: "#6B7280", letterSpacing: "0.4px", textTransform: "uppercase", borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, idx) => (
                    <tr key={inv.id} data-testid={`queue-row-${idx}`}
                      style={{ borderBottom: idx < invoices.length - 1 ? "1px solid #F3F4F6" : "none", cursor: "pointer", transition: "background 0.1s" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={{ padding: "14px 16px" }}>
                        <span style={{ fontFamily: "monospace", fontSize: "12px", fontWeight: 600, color: "#1A1A1A" }}>
                          {inv.ap_reference || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "14px 16px", fontSize: "13px", color: "#374151" }}>
                        {inv.supplier_invoice_number || "—"}
                      </td>
                      <td style={{ padding: "14px 16px", fontSize: "12px", color: "#6B7280", whiteSpace: "nowrap" }}>
                        {fmtDate(inv.submitted_at)}
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "right", fontSize: "13px", fontWeight: 600, color: "#1A1A1A" }}>
                        {fmt(inv.total_minor, inv.currency_code)}
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <StatusBadge status={inv.status} />
                      </td>
                      <td style={{ padding: "8px 16px" }}>
                        {inv.status === "SUBMITTED" && (
                          <button
                            data-testid={`trigger-match-btn-${idx}`}
                            disabled={!!triggering[inv.id]}
                            onClick={(e) => { e.stopPropagation(); triggerMatch(inv.id); }}
                            style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "5px 10px", background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#166534", borderRadius: "6px", fontSize: "11px", fontWeight: 600, cursor: "pointer" }}
                          >
                            <Play size={11} />
                            {triggering[inv.id] ? "Matching…" : "Run Match"}
                          </button>
                        )}
                        {["MATCHED", "EXCEPTION"].includes(inv.status) && (
                          <span style={{ fontSize: "11px", color: inv.status === "MATCHED" ? "#166534" : "#9A3412", fontWeight: 600 }}>
                            {inv.status === "MATCHED" ? "Ready to Approve" : "Needs Review"}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "right" }}
                        onClick={() => navigate(`/ap/invoice-review/${inv.id}`)}>
                        <ChevronRight size={16} style={{ color: "#C9A15F" }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {pages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "20px" }}>
            {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)}
                style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid", cursor: "pointer", fontSize: "13px", background: page === p ? "#1A1A1A" : "transparent", color: page === p ? "#F5EFE6" : "#6B5B4A", borderColor: page === p ? "#1A1A1A" : "#DDD" }}>
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
